import { formatElapsedClock } from "../../utilities/duration";

/** Assinatura mínima do `t` do react-i18next usada aqui. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Formata o tempo decorrido no estado como relógio digital "HH:MM:SS".
 * A partir de 24h a contagem ganha o prefixo de dias (`2D 10:17:33`) em vez de
 * seguir somando horas — ver `utilities/duration.ts`.
 * `null` (vaga sem evento) cai em "Sem evento".
 *
 * `compact` esconde os segundos quando há dias, para os cards estreitos do mapa
 * compacto (onde "2D 10:17:33" estouraria a largura).
 */
export const formatElapsed = (
  totalSeconds: number | null,
  t: TranslateFn,
  options?: { compact?: boolean }
): string => {
  if (totalSeconds === null || totalSeconds === undefined) {
    return t("noEvent");
  }
  return formatElapsedClock(totalSeconds, options);
};

/** Relógio local HH:MM:SS a partir de um epoch (ms). */
export const formatClock = (epochMs: number): string =>
  new Date(epochMs).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

/**
 * Data + hora local a partir de um ISO string (ou "—" se ausente).
 *
 * A data acompanha a hora porque uma vaga pode ficar dias no mesmo estado — só
 * "14:23:45" não dizia de QUANDO era o último evento. Formato curto e numérico
 * (`04/09/2026 14:23:45`) de propósito: o `.detailRow` do painel tem ~270px e
 * não usa nowrap, então nome de mês/dia da semana empurraria o valor para uma
 * segunda linha e desalinharia a coluna chave/valor.
 *
 * `locale` segue o idioma do app (pt-BR / en-US). Sem ele cai no locale do
 * browser, que é o comportamento anterior.
 */
export const formatIsoDateTime = (iso: string | null, locale?: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${date} ${time}`;
};
